import React, { useEffect, useRef, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { useSnackbar } from 'notistack';
import ReactTooltip from 'react-tooltip';

import Layout from '../../../layout/Layout';
import PermissionsGate from '../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../common/permissionGroup';
import AccessDenied from '../../../components/AccessDenied/AccessDenied';
import Card from '../../../components/Card/Card';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Pagination from '../../../components/Pagination/Pagination';
import Table from '../../../components/Table/Table';
import TableHead from '../../../components/Table/TableHead';
import TableRow from '../../../components/Table/TableRow';
import TableHeadData from '../../../components/Table/TableHeadData';
import TableBody from '../../../components/Table/TableBody';
import TableData from '../../../components/Table/TableData';
import EmptyTable from '../../../components/Table/EmptyTable';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import { coWarehouseTable } from '../../../constants/coWarehouseTable';
import getWarehouse from '../../../thunks/coilovers/warehouse/getWarehouse';
import Text from '../../../components/Text/Text';
import Badge from '../../../components/Badge/Badge';
import warehousePartsStatus, { warehouseStatusInfo } from '../../../constants/warehousePartsStatus';
import { setWarehouseFilter } from '../../../actions/coilovers/warehouse/warehouseFilter';
import WarehouseActions from './components/WarehouseActions';
import {
  IconAlertCircle, IconCart, IconCopy, IconCross, IconInfo, IconWarningTriangle
} from '../../../assets/icon';
import { clearDeleteWarehouseParts, clearPatchWarehouseParts } from '../../../actions/coilovers/warehouse/patchWarehouse';
import deleteWarehouse from '../../../thunks/coilovers/warehouse/deleteWarehouse';
import { setApplicationState } from '../../../actions/application/application';
import toggleArrayItem from '../../../helpers/toggleArrayItem';
import WarehouseSubheader from './components/WarehouseSubheader';
import useQueryParams from '../../../hooks/useQueryParams';
import WarehouseEditModal from '../../../components/WarehouseEditModal/WarehouseEditModal';
import WarehouseHeaderRight from './components/WarehouseHeaderRight';
import PriceHistoryModal from '../../../components/PriceHistoryModal/PriceHistoryModal';
import ButtonTypes from '../../../constants/buttonTypes';
import getWarehouseDuplicates from '../../../thunks/coilovers/warehouse/getWarehouseDuplicates';
import CoOrderNewOrderModal from '../../../components/CoOrderNewOrderModal/CoOrderNewOrderModal';
import { setCreateNewOrderData } from '../../../thunks/coilovers/orders/createNewOrder';
import WarehouseStockHistoryModal from './components/WarehouseStockHistoryModal';
import SupplierAvailabilityModal from './components/SupplierAvailabilityModal';
import SupplierOrderModal from './components/SupplierOrderModal';
import Button from '../../../components/Button/Button';

const searchHeaderClassName = 'w-[250px]';
const tableContainerClassName = 'overflow-auto';
const actionCellClassName = 'w-[60px] py-0 pl-0 pr-[9px]';
const tableCellClassName = 'min-w-max whitespace-nowrap px-[9px] py-0';

const Warehouse = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    getWarehouse: {
      loading, data, currentPage, lastPage
    },
    getWarehouseDuplicates: {
      data: duplicatesData
    },
    deleteWarehouse: { id: deletedId, error: deleteError, loading: deleteLoading },
    warehouseFilter: {
      query,
      size,
      order,
      brands,
    },
    warehouseFilter,
    application: { selectedRow, warehouseTableSetting }
  } = useSelector((state) => ({
    getWarehouse: state?.getWarehouse,
    getWarehouseDuplicates: state?.getWarehouseDuplicates,
    warehouseFilter: state?.warehouseFilter,
    deleteWarehouse: state?.deleteWarehouse,
    application: state?.application
  }), shallowEqual);
  const [editModalVisibility, setEditModalVisibility] = useState();
  const [deleteModalVisibility, setDeleteModalVisibility] = useState();
  const [priceHistoryModalVisibility, setPriceHistoryModalVisibility] = useState();
  const [newOrderModalVisible, setNewOrderModalVisible] = useState(false);
  const [historyItem, setHistoryItem] = useState();
  const [editItem, setEditItem] = useState({});
  const [deleteId, setDeleteId] = useState();
  const [currentPriceHistory, setCurrentPriceHistory] = useState();
  const [supplierAvailabilityItem, setSupplierAvailabilityItem] = useState();
  const [supplierOrderVisible, setSupplierOrderVisible] = useState(false);
  const [supplierCart, setSupplierCart] = useState([]);
  const [queryParams, setQueryParams] = useQueryParams();
  const initialQueryRef = useRef(queryParams.query || query);

  useEffect(() => {
    const filterData = {
      ...(queryParams.query?.length && { query: queryParams.query }),
      ...(queryParams.order?.length && { order: queryParams.order }),
      ...(queryParams.brands?.length && { brands: queryParams.brands.split(',') }),
    };

    batch(() => {
      dispatch(setWarehouseFilter(filterData));
      dispatch(getWarehouse());
      dispatch(getWarehouseDuplicates());
    });
    return batch(() => {
      dispatch(clearDeleteWarehouseParts());
      dispatch(clearPatchWarehouseParts());
    });
  }, []);

  useEffect(() => {
    if (query === initialQueryRef.current) {
      return undefined;
    }

    initialQueryRef.current = query;
    const timer = setTimeout(() => batch(() => {
      dispatch(setWarehouseFilter({ page: 1 }));
      dispatch(getWarehouse());
    }), 800);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (deletedId) {
      enqueueSnackbar('Part delete successfully', { variant: 'success' });
      dispatch(clearDeleteWarehouseParts());
      setDeleteModalVisibility(false);
    }
    if (deleteError) {
      enqueueSnackbar(deleteError, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        persist: true,
        action: (id) => (
          <div role="presentation" onClick={() => closeSnackbar(id)}>
            <IconCross />
          </div>
        )
      });
    }
  }, [deletedId, deleteError]);

  useEffect(() => {
    setQueryParams({
      ...(query && { query }),
      ...(order && { order }),
      ...(brands?.length && { brands: brands.join(',') }),
    });
  }, [warehouseFilter]);

  const onPagePress = (page) => batch(() => {
    batch(() => {
      dispatch(setWarehouseFilter({ page }));
      dispatch(getWarehouse());
    });
  });

  const onSizeChange = ({ value }) => {
    batch(() => {
      dispatch(setWarehouseFilter({ size: value }));
      dispatch(getWarehouse());
    });
  };

  const onSort = (isSort, sortStr) => {
    if (isSort) {
      batch(() => {
        dispatch(setWarehouseFilter({ order: sortStr }));
        dispatch(getWarehouse());
      });
    }
  };

  const selectRow = (id) => {
    dispatch(setApplicationState({
      selectedRow: toggleArrayItem(selectedRow, id)
    }));
  };

  const getColumn = () => {
    if (warehouseTableSetting?.length) {
      return coWarehouseTable.filter(({ source }) => warehouseTableSetting.includes(source));
    }
    return coWarehouseTable;
  };

  const onDuplicateItemClick = (item) => {
    dispatch(setWarehouseFilter({ query: item }));
  };

  const onToOrderOverstock = (mpn) => {
    dispatch(setCreateNewOrderData({
      firstName: 'CO',
      lastName: 'Warehouse',
      address: '4699 Nautilus Ct S 503',
      address2: 'M-F 9-3PM',
      country: 'US',
      state: 'CO',
      zip: '80301',
      city: 'Boulder',
      phone: '8889041411',
      mpn,
    }));
    setNewOrderModalVisible(!newOrderModalVisible);
  };

  const addSupplierItem = (item) => {
    if (supplierCart.some(({ external_item_id: itemId, supplier }) => (
      itemId === item.external_item_id && supplier === item.supplier
    ))) {
      enqueueSnackbar(`${item.mpn} is already in the ${item.supplier} order`, { variant: 'info' });
      setSupplierAvailabilityItem(undefined);
      return;
    }
    setSupplierCart((currentCart) => {
      return [...currentCart, item];
    });
    enqueueSnackbar(`${item.mpn} added to ${item.supplier} order`, { variant: 'success' });
    setSupplierAvailabilityItem(undefined);
  };

  return (
    <Layout pageTitle="Warehouse" subheader={<WarehouseSubheader />}>
      <PermissionsGate
        can={permissions.COW_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader
          headerLeft={(
            <div className={searchHeaderClassName}>
              <SearchInput
                isClearable
                value={query}
                onChange={({ target: { value } }) => dispatch(setWarehouseFilter({ query: value }))}
              />
            </div>
          )}
          headerRight={(
            <WarehouseHeaderRight
              cartCount={supplierCart.length}
              onOpenSupplierOrder={() => setSupplierOrderVisible(true)}
            />
          )}
          headerBordered={false}
          footerBordered={false}
          footer={(
            <div className="flex justify-end">
              <Pagination
                currentPage={currentPage}
                onPagePress={onPagePress}
                lastPage={lastPage}
                withPaginationSize
                onSizeChange={onSizeChange}
                size={size}
              />
            </div>
          )}
        >
          {duplicatesData?.length > 0 && (
            <div className="flex flex-row gap-[10px]">
              <div
                data-tip=""
                data-for="duplicates_tooltip"
              >
                <IconCopy color="#F1416C" />
              </div>
              <ReactTooltip
                id="duplicates_tooltip"
                place="top"
                effect="solid"
              >
                We found some duplicates. Please check it
              </ReactTooltip>
              {duplicatesData?.map((item) => (
                <div
                  role="presentation"
                  key={item}
                  className="cursor-pointer"
                  onClick={() => onDuplicateItemClick(item)}
                >
                  <Badge text={item} type={ButtonTypes.LIGHT_DANGER} />
                </div>
              ))}
            </div>
          )}
          <div className={tableContainerClassName}>
            <Table loading={loading}>
              <TableHead>
                <TableRow bordered>
                  <PermissionsGate can={permissions.COW_UPDATE}>
                    <TableHeadData />
                  </PermissionsGate>
                  {
                    getColumn()
                      .map(({
                        title,
                        source,
                        sortable
                    }) => (
                      <TableHeadData
                        key={source}
                        sortable={sortable}
                        order={order}
                        source={source}
                        onSort={(val) => onSort(sortable, val)}
                      >
                        {title}
                      </TableHeadData>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length ? data.map(({
                  _id,
                  part_whs_id: id,
                  part_whs_status: status,
                  part_whs_type: type,
                  part_whs_brand: brand,
                  part_whs_mpn: mpn,
                  part_whs_total_qty: totalQty,
                  part_whs_available_qty: availableQty,
                  part_whs_reserved_qty: reservedQty,
                  part_whs_need_for_order_qty: forOrderQty,
                  part_whs_ordered_qty: orderedQty,
                  part_whs_min_qty: minQty,
                  part_whs_max_qty: maxQty,
                  part_whs_ordered_needed_status: neededStatus,
                  part_whs_to_order: toOrder,
                  part_whs_price: price,
                  part_whs_total_used: used,
                  part_whs_average_monthly_used: monthlyUsed,
                  part_whs_return_qty: returnQty,
                  part_whs_location: location,
                  part_whs_note: note,
                  part_whs_shipping_length: shippingLength,
                  part_whs_shipping_width: shippingWidth,
                  part_whs_shipping_height: shippingHeight,
                  part_whs_shipping_weight: shippingWeight,
                  part_whs_order_for_orders: orderForOrders,
                  part_whs_included_type: includedType,
                  part_whs_price_history: priceHistory,
                  part_whs_is_overstock: isOverstock,
                }, index) => {
                  return (
                    <TableRow
                      key={`tr_${_id}`}
                      bordered={index < data.length - 1}
                      selected={selectedRow.includes(_id)}
                      onClick={() => selectRow(_id)}
                    >
                      <PermissionsGate can={permissions.COW_UPDATE}>
                        <TableData className={actionCellClassName}>
                          <WarehouseActions
                            side={index < data.length - 3 ? 'right' : 'top'}
                            onEdit={() => {
                              setEditItem(data[index]);
                              setEditModalVisibility(true);
                            }}
                            onDelete={() => {
                              setDeleteId(_id);
                              setDeleteModalVisibility(true);
                            }}
                            onHistory={() => setHistoryItem(data[index])}
                            onToOrder={() => {
                              if (isOverstock === 'yes') {
                                onToOrderOverstock(mpn);
                              } else {
                                const url = `https://manage-markets.com/UI/OrderCreator/?itemPast=${brand},${mpn},1`;
                                window.open(url, '_blank').focus();
                              }
                            }}
                          />
                        </TableData>
                      </PermissionsGate>
                      {
                        warehouseTableSetting.includes('part_whs_id')
                        && (
                          <TableData className={tableCellClassName}>{id || ''}</TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_status')
                        && (
                          <TableData className={tableCellClassName}>
                            {status
                              && (
                                <Badge
                                  text={availableQty > 0
                                    ? warehousePartsStatus.IN_STOCK : warehousePartsStatus.OUT_OF_STOCK}
                                  type={availableQty > 0
                                    ? warehouseStatusInfo?.[warehousePartsStatus.IN_STOCK]
                                      ?.state : warehouseStatusInfo?.[warehousePartsStatus.OUT_OF_STOCK]?.state}
                                />
                              )}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_brand')
                        && (
                          <TableData className={tableCellClassName}>
                            {brand}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_type')
                        && (
                          <TableData className={tableCellClassName}>
                            {type}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_mpn')
                        && (
                          <TableData className={tableCellClassName}>
                            <div className="flex flex-row items-center">
                              <Text text={mpn || ''} bold />
                              {(!brand || !type || type === 'Not Find' || brand === 'Other') && (
                                <div
                                  data-for={`missing-part-data-${_id}`}
                                  data-tip=""
                                >
                                  <IconWarningTriangle
                                    color="#FFB885"
                                    width={30}
                                    className="flex"
                                    data-for={`missing-part-data-${_id}`}
                                  />
                                  <ReactTooltip
                                    id={`missing-part-data-${_id}`}
                                    place="top"
                                  >
                                    Missing important part data
                                  </ReactTooltip>
                                </div>
                              )}
                              <PermissionsGate can={permissions.SUPPLIER_ORDERS_CREATE}>
                                <div
                                  className="ml-[8px]"
                                  role="presentation"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <Button
                                    icon={<IconCart className="w-[16px] h-[16px]" />}
                                    type={ButtonTypes.LIGHT_PRIMARY}
                                    containerStyle="p-[5px]"
                                    tooltip="Add from supplier"
                                    tooltipId={`supplier-stock-${_id}`}
                                    disabled={!mpn}
                                    onClick={() => setSupplierAvailabilityItem({ mpn, brand, type })}
                                  />
                                </div>
                              </PermissionsGate>
                            </div>
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_included_type')
                        && (
                          <TableData className={tableCellClassName}>
                            {includedType}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_total_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {totalQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_available_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {availableQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_reserved_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {reservedQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_average_monthly_used')
                        && (
                          <TableData className={tableCellClassName}>
                            {monthlyUsed}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_ordered_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            <div className="flex justify-center items-center">
                              <a
                                href={`/coilovers/ordered-parts/?query=${mpn}`}
                                className="globalLink"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {orderedQty}
                              </a>
                            </div>
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_need_for_order_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {forOrderQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_min_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {minQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_max_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {maxQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_ordered_needed_status')
                        && (
                          <TableData className={tableCellClassName}>
                            {neededStatus}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_to_order')
                        && (
                          <TableData className={tableCellClassName}>
                            {toOrder}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_order_for_orders')
                        && (
                          <TableData className={tableCellClassName}>
                            <div className="flex justify-center items-center">
                              <a
                                href={`/coilovers/orders/?query=${mpn}`}
                                className="globalLink"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {orderForOrders}
                              </a>
                            </div>
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_price')
                        && (
                          <TableData className={tableCellClassName}>
                            {price ? (
                              <div className="flex flex-row justify-center items-center">
                                $
                                {price}
                                {priceHistory && (
                                  <IconInfo
                                    width={20}
                                    onClick={() => {
                                      setCurrentPriceHistory(priceHistory);
                                      setPriceHistoryModalVisibility(true);
                                    }}
                                  />
                                )}
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer"
                                role="presentation"
                                onClick={() => {
                                  setEditItem(data[index]);
                                  setEditModalVisibility(true);
                              }}
                              >
                                <IconAlertCircle />
                              </div>
                            )}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_shipping_length')
                        && (
                          <TableData className={tableCellClassName}>
                            {shippingLength}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_shipping_width')
                        && (
                          <TableData className={tableCellClassName}>
                            {shippingWidth}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_shipping_height')
                        && (
                          <TableData className={tableCellClassName}>
                            {shippingHeight}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_shipping_weight')
                        && (
                          <TableData className={tableCellClassName}>
                            {shippingWeight}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_total_used')
                        && (
                          <TableData className={tableCellClassName}>
                            {used}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_return_qty')
                        && (
                          <TableData className={tableCellClassName}>
                            {returnQty}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_location')
                        && (
                          <TableData className={tableCellClassName}>
                            {location}
                          </TableData>
                        )
                      }
                      {
                        warehouseTableSetting.includes('part_whs_note')
                        && (
                          <TableData className={tableCellClassName}>
                            {note}
                          </TableData>
                        )
                      }
                    </TableRow>
                  );
                })
                  : (
                    <EmptyTable colSpan={getColumn().length + 1} text="No any data find" />
                  )}
              </TableBody>
            </Table>
          </div>
        </Card>
        {editItem && (
          <WarehouseEditModal
            visible={editModalVisibility}
            toggleVisible={() => setEditModalVisibility(false)}
            item={editItem}
          />
        )}
        {deleteId && (
          <ConfirmModal
            visible={deleteModalVisibility}
            toggleVisible={() => setDeleteModalVisibility(false)}
            confirmButtonText="Delete"
            confirmText="Part will be deleted permanently"
            onConfirm={() => dispatch(deleteWarehouse(deleteId))}
            loading={deleteLoading}
            onCancel={() => {
              setDeleteId();
              dispatch(clearDeleteWarehouseParts());
            }}
          />
        )}
        {currentPriceHistory && (
          <PriceHistoryModal
            toggleVisible={() => setPriceHistoryModalVisibility(!priceHistoryModalVisibility)}
            visible={priceHistoryModalVisibility}
            data={currentPriceHistory}
          />
        )}
        {historyItem && (
          <WarehouseStockHistoryModal
            visible={Boolean(historyItem)}
            toggleVisible={() => setHistoryItem()}
            item={historyItem}
          />
        )}
        <CoOrderNewOrderModal
          visible={newOrderModalVisible}
          toggleVisible={() => setNewOrderModalVisible(!newOrderModalVisible)}
        />
        <SupplierAvailabilityModal
          visible={Boolean(supplierAvailabilityItem)}
          item={supplierAvailabilityItem}
          onClose={() => setSupplierAvailabilityItem(undefined)}
          onAdd={addSupplierItem}
        />
        <SupplierOrderModal
          visible={supplierOrderVisible}
          items={supplierCart}
          onClose={() => setSupplierOrderVisible(false)}
          onChangeQuantity={(supplier, externalItemId, quantity) => setSupplierCart(
            supplierCart.map((item) => (item.external_item_id === externalItemId
              && item.supplier === supplier
              ? { ...item, quantity }
              : item))
          )}
          onChangeWarehouse={(supplier, externalItemId, warehouseCode, purchaseCost) => setSupplierCart(
            supplierCart.map((item) => (item.external_item_id === externalItemId
              && item.supplier === supplier
              ? {
                ...item,
                warehouse_code: warehouseCode,
                purchase_cost: purchaseCost ?? item.purchase_cost,
              }
              : item))
          )}
          onRemove={(supplier, externalItemId) => setSupplierCart(
            supplierCart.filter((item) => item.external_item_id !== externalItemId
              || item.supplier !== supplier)
          )}
          onOrdered={({ supplier }) => {
            setSupplierCart((currentCart) => currentCart.filter(
              (item) => item.supplier !== supplier
            ));
            setSupplierOrderVisible(false);
          }}
        />
      </PermissionsGate>
    </Layout>
  );
};

export default Warehouse;
